// src/components/feedback/ErrorBoundary.jsx
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Divider,
  Collapse,
  IconButton,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Refresh as RefreshIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';

// Styled error container
const ErrorContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  maxWidth: '800px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  overflow: 'hidden',
}));

// Styled error icon container with error type colors
const ErrorIconContainer = styled(Box)(({ theme, severity }) => {
  const colors = {
    critical: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };
  
  const color = colors[severity] || colors.critical;
  
  return {
    backgroundColor: `${color}10`,
    color: color,
    borderRadius: '50%',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  };
});

// Error details for development mode
const ErrorDetails = ({ error, errorInfo }) => {
  const [expanded, setExpanded] = React.useState(false);
  const theme = useTheme();
  
  if (!error) return null;
  
  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Error Details
        </Typography>
        
        <IconButton 
          size="small" 
          onClick={() => setExpanded(!expanded)}
          sx={{ color: theme.palette.primary.main }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box 
          sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: theme.palette.background.default,
            // borderRadius: theme.shape.borderRadius,
            overflow: 'auto',
            maxHeight: '300px',
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Error: {error.toString()}
          </Typography>
          
          {errorInfo && (
            <>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
                Component Stack:
              </Typography>
              <Typography 
                variant="body2" 
                component="pre" 
                sx={{ 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-word',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  lineHeight: 1.5,
                  mt: 1
                }}
              >
                {errorInfo.componentStack}
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Fallback UI component
const ErrorFallback = ({ 
  error, 
  errorInfo, 
  resetError, 
  navigateToHome,
  navigateBack,
  severity,
  title,
  message
}) => {
  const theme = useTheme();
  
  // Use provided title/message or defaults based on the error
  const errorTitle = title || 'Something went wrong';
  const errorMessage = message || 'We encountered an unexpected error. Please try again or contact support if the problem persists.';
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        p: 3,
      }}
    >
      <ErrorContainer>
        <ErrorIconContainer severity={severity}>
          <BugReportIcon sx={{ fontSize: 48 }} />
        </ErrorIconContainer>
        
        <Typography variant="h4" component="h2" gutterBottom align="center">
          {errorTitle}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: '500px', mb: 4 }}>
          {errorMessage}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {resetError && (
            <Button
              variant="contained"
              
              onClick={resetError}
              startIcon={<RefreshIcon />}
              isGlowing
            >
              Try Again
            </Button>
          )}
          
          {navigateToHome && (
            <Button
              variant="outlined"
              
              onClick={navigateToHome}
              startIcon={<HomeIcon />}
            >
              Go to Home
            </Button>
          )}
          
          {navigateBack && (
            <Button
              variant="outlined"
              
              onClick={navigateBack}
              startIcon={<ArrowBackIcon />}
            >
              Go Back
            </Button>
          )}
        </Box>
        
        {/* Show error details only in development mode */}
        {process.env.NODE_ENV === 'development' && (
          <ErrorDetails error={error} errorInfo={errorInfo} />
        )}
      </ErrorContainer>
    </Box>
  );
};

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service here
    this.setState({ errorInfo });
    
    // Call the onError prop if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }
  }
  
  resetError = () => {
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // Call onReset prop if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  }
  
  navigateToHome = () => {
    // Navigate to home page
    if (this.props.onNavigateHome) {
      this.props.onNavigateHome();
    } else {
      window.location.href = '/';
    }
    this.resetError();
  }
  
  navigateBack = () => {
    // Navigate back in history
    if (this.props.onNavigateBack) {
      this.props.onNavigateBack();
    } else {
      window.history.back();
    }
    this.resetError();
  }
  
  render() {
    const { hasError, error, errorInfo } = this.state;
    const { 
      children, 
      fallback, 
      fallbackRender,
      renderFallback,
      showReset = true,
      showHome = true,
      showBack = true,
      severity = 'critical',
      title,
      message
    } = this.props;
    
    if (hasError) {
      // Custom fallback renderer takes priority
      if (fallbackRender) {
        return fallbackRender({ error, errorInfo, resetError: this.resetError });
      }
      
      // Legacy renderFallback support
      if (renderFallback) {
        return renderFallback({ error, errorInfo, resetError: this.resetError });
      }
      
      // Custom fallback component
      if (fallback) {
        return React.cloneElement(fallback, { 
          error, 
          errorInfo, 
          resetError: this.resetError 
        });
      }
      
      // Default fallback UI
      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetError={showReset ? this.resetError : undefined}
          navigateToHome={showHome ? this.navigateToHome : undefined}
          navigateBack={showBack ? this.navigateBack : undefined}
          severity={severity}
          title={title}
          message={message}
        />
      );
    }
    
    // Render children if no error
    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onError: PropTypes.func,
  onReset: PropTypes.func,
  onNavigateHome: PropTypes.func,
  onNavigateBack: PropTypes.func,
  fallback: PropTypes.element,
  fallbackRender: PropTypes.func,
  renderFallback: PropTypes.func, // Legacy name for fallbackRender
  showReset: PropTypes.bool,
  showHome: PropTypes.bool,
  showBack: PropTypes.bool,
  severity: PropTypes.oneOf(['critical', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.string,
};

// Export both the main component and the fallback
export { ErrorFallback };
export default ErrorBoundary;